import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { message, Tag, Switch, Tooltip, DatePicker } from 'antd';
import { WarningOutlined, CloseCircleOutlined, CheckCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import DataManagementApi from 'apis/api/DataManagement';
import ModalComponent from '@components/common/Modal';
import Table, { IColumnTable } from '@components/table-devextreme/Table';
import { publicUrl } from '@utils/env';
import { TRA_HANG_IMPORT_SDT } from '@lib/featureFlags';
import viVN from 'antd/es/locale/vi_VN';
import 'moment/locale/vi';

moment.locale('vi');

interface IProps {
  isOpen: boolean;
  year: number;
  month: number;
  callBackClose: () => void;
  callBackRefresh: () => void;
}

interface IServerRowError {
  rowNo: number;
  message: string;
}

type TErrorTab = 'file' | 'server' | 'warn';

/* ------------------------------------------------------------------ */
/* Cấu trúc file mẫu "Trực nhập kho 2026.xlsx"                         */
/*  A NGÀY | B PN (chỉ STT phiếu) | C TÊN HÀNG | D ĐV TÍNH | E SL NHẬP */
/*  F USERNAME | G KINH DOANH | H SĐT KHÁCH HÀNG | I ĐẠI LÝ | J MÃ ĐƠN */
/*  K GHI CHÚ | L SERI NHẬN | M SỐ XE | N SERI SIM | O SỐ SIM          */
/*  P NHÓM HÀNG                                                         */
/*  File mới bỏ ĐƠN GIÁ, THÀNH TIỀN, MÃ KHÁCH HÀNG: server tự lấy mã KH */
/*  + tên KH từ SĐT. COLUMN_MAP vẫn giữ các cột cũ để file cũ đọc được. */
/*  Cờ TRA_HANG_IMPORT_SDT = false -> quay về file mẫu + logic cũ.      */
/*                                                                      */
/*  Lưu ý: cột H "KINH DOANH" chỉ là TÊN hiển thị (vd: "huy", "chánh") */
/*  để kế toán dễ nhìn/đối chiếu, KHÔNG dùng để suy ra tài khoản hệ    */
/*  thống vì tên có thể trùng hoặc không khớp định dạng username.      */
/*  Cột S "USERNAME" mới thêm ở CUỐI file (không chèn giữa để khỏi  */
/*  phá công thức SUMIFS/VLOOKUP của các sheet TH / nhóm hàng đang trỏ */
/*  theo cột H, R) mới là giá trị thật sự gửi lên server (saleAccount).*/
/*  saleAccount là trường TỰ NHẬP TAY, BẮT BUỘC trên từng dòng (không */
/*  kéo xuống theo cụm serial như Mã KH/Mã đơn/Đại lý).                */
/*                                                                      */
/*  Lưu ý thêm về cột "NHÓM HÀNG": trong file thường có 1 sheet riêng   */
/*  tên "nhóm hàng" (cột A = Tên hàng, cột B = CODE) dùng để kế toán    */
/*  tra cứu bằng mắt / dropdown khi nhập liệu. Nếu người nhập liệu bỏ   */
/*  trống ô NHÓM HÀNG trên sheet dữ liệu, ta tự tra theo TÊN HÀNG ở     */
/*  sheet này để điền hộ (xem loadProductCodeLookup + phần cluster).    */
/* ------------------------------------------------------------------ */
const COLUMN_MAP: Record<string, string> = {
  NGÀY: 'returnDate',
  PN: 'voucherNo',
  'TÊN HÀNG': 'productName',
  'ĐV TÍNH': 'unit',
  'SL NHẬP': 'quantity',
  'ĐƠN GIÁ': 'unitPrice',
  'THÀNH TIỀN': 'amount',
  'KINH DOANH': 'saleName', // chỉ để hiển thị, KHÔNG gửi server
  'MÃ KHÁCH HÀNG': 'customerCode',
  'TÊN KHÁCH HÀNG': 'customerName',
  'SĐT KHÁCH HÀNG': 'customerPhoneNum',
  'ĐẠI LÝ': 'distributorName',
  'MÃ ĐƠN': 'orderCode',
  'GHI CHÚ': 'note',
  'SERI NHẬN': 'serial',
  'SỐ XE': 'carNo',
  'SERI SIM': 'simSerial',
  'SỐ SIM': 'simNo',
  'TÌNH TRẠNG': 'status',
  'NHÓM HÀNG': 'productCode',
  USERNAME: 'saleAccount' // giá trị thật gửi lên server
};

/** Tên sheet tra cứu Mã sản phẩm theo Tên hàng (cột A = Tên hàng, cột B = CODE).
 *  So khớp không phân biệt hoa/thường qua normalizeHeader nên "nhóm hàng",
 *  "Nhóm Hàng"... đều nhận diện được, miễn đúng chữ (có dấu). */
const PRODUCT_LOOKUP_SHEET_NAME = 'NHÓM HÀNG';

/** Các field chỉ dùng để xem trước, không gửi lên server */
const PREVIEW_ONLY_FIELDS = [
  'key',
  'excelRow',
  'rowNo',
  'serverError',
  'missingCustomer',
  'missingSaleAccount',
  // 'unit',
  'unitPrice',
  'amount',
  'note',
  'carNo',
  'status',
  'saleName',
  'rawQuantity',
  'returnDateExport',
  'productCodeGuessed',
  // Server cũ chưa có 3 trường này -> chỉ để xem, không gửi
  ...(TRA_HANG_IMPORT_SDT ? [] : ['distributorName', 'customerName', 'customerPhoneNum'])
];

/** File mẫu đặt trong thư mục public/files - đổi tên file cho khớp dự án */
const SAMPLE_FILE_URL = `${publicUrl}/files/${
  TRA_HANG_IMPORT_SDT ? 'File_Tra_Hang_Mau_.xlsx' : 'File_Tra_Hang_Mau_cu.xlsx'
}`;

/** accept của input file, dùng MIME cho chắc (giống màn Import khách hàng) */
const EXCEL_ACCEPT = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';

/** Bắt buộc mỗi dòng phải có Mã khách hàng HOẶC Mã đơn (server cũng chặn điều kiện này).
 *  Đổi thành false nếu cần tạm tắt để test. */
const REQUIRE_CUSTOMER_OR_ORDER = true;

/** Bắt buộc mỗi dòng phải có Username kinh doanh (saleAccount) để server detach đúng
 *  tài khoản nhân viên kinh doanh. Đổi thành false nếu muốn tạm cho phép để trống. */
const REQUIRE_SALE_ACCOUNT = true;

/** Chuẩn hoá khoảng trắng: bỏ NBSP, zero-width, gộp space thừa rồi trim 2 đầu */
const cleanSpace = (v: any) =>
  `${v ?? ''}`
    .replace(/[\u00A0\u200B\uFEFF]/g, ' ') // NBSP, zero-width space, BOM -> space thường
    .replace(/\s+/g, ' ') // gộp nhiều space thành 1
    .trim();

/** Với mã (KH, PN, mã đơn, nhóm hàng, username KD...): bỏ HẲN mọi khoảng trắng bên trong */
const cleanCode = (v: any) => cleanSpace(v).replace(/\s+/g, '');

/** Ô SĐT kiểu số trong Excel bị mất số 0 đầu: 909646465 -> '0909646465', 2743686868 -> '02743686868'.
 *  Ô kiểu chữ giữ nguyên. */
const toPhone = (v: any) => {
  const phone = cleanCode(v);
  if (typeof v === 'number' && phone) return `0${phone}`;
  return phone;
};

const normalizeHeader = (v: any) => `${v ?? ''}`.replace(/\s+/g, ' ').trim().toUpperCase();

/** Chuẩn hoá TÊN HÀNG để làm khoá tra cứu: gộp space + hạ chữ thường.
 *  Không strip dấu tiếng Việt vì bảng tra cứu và sheet dữ liệu thường do cùng
 *  một người gõ nên khả năng lệch dấu thấp; nếu cần "mờ" hơn có thể bổ sung
 *  bước bỏ dấu ở đây sau. */
const normalizeProductName = (v: any) => cleanSpace(v).toLowerCase();

const toText = (v: any) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return String(v);
  return cleanSpace(v);
};

const toNumber = (v: any) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(`${v}`.replace(/[,\s]/g, ''));
  return Number.isNaN(n) ? null : n;
};

const toDate = (value: any) => {
  if (!value) return null;
  if (value instanceof Date) return moment(value).format('YYYY-MM-DDT00:00:00');
  if (typeof value === 'number') {
    const d = XLSX.SSF.parse_date_code(value);
    if (!d) return null;
    return moment(`${d.y}-${d.m}-${d.d}`, 'YYYY-M-D').format('YYYY-MM-DDT00:00:00');
  }
  const m = moment(`${value}`.trim(), ['DD/MM/YYYY', 'YYYY-MM-DD', 'D/M/YYYY'], true);
  return m.isValid() ? m.format('YYYY-MM-DDT00:00:00') : null;
};

/** Ký hiệu chi nhánh dùng làm tiền tố số phiếu nhập, nhận diện theo TÊN chi nhánh của tài khoản
 *  (tên đã bỏ dấu, chữ thường). Vd chi nhánh "Kho Hồ Chí Minh" -> "kho ho chi minh" -> SG.
 *  shortcut: API chi nhánh chưa có trường mã nên dò theo tên, khi backend trả mã chi nhánh thì dùng thẳng mã đó. */
const BRANCH_PREFIX_RULES: { prefix: string; keywords: string[] }[] = [
  { prefix: 'SG', keywords: ['ho chi minh', 'hcm', 'sai gon'] },
  { prefix: 'HN', keywords: ['ha noi'] },
  { prefix: 'DN', keywords: ['da nang'] },
  { prefix: 'CT', keywords: ['can tho'] }
];

const getBranchPrefix = (branchName: any) => {
  const name = `${branchName || ''}`.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();
  let prefix = '';
  BRANCH_PREFIX_RULES.forEach(rule => {
    if (prefix) return;
    rule.keywords.forEach(k => {
      if (name.includes(k)) prefix = rule.prefix;
    });
  });
  return prefix;
};

/** Số PN trong file KT kho chỉ là STT phiếu -> ghép thành <chi nhánh><yy><mm><STT 3 số>.
 *  Vd: chi nhánh SG, ngày 29/10/2026, PN 102 -> "SG2610102"; PN 5 -> "SG2610005".
 *  PN không phải toàn số (file cũ đã ghi sẵn "SG2610102") thì giữ nguyên. */
const buildVoucherNo = (prefix: string, returnDate: string | null, pn: string) => {
  if (!pn || !/^\d+$/.test(pn)) return pn;
  if (!prefix || !returnDate) return pn; // thiếu chi nhánh / ngày đã có lỗi riêng chặn import
  return `${prefix}${moment(returnDate).format('YYMM')}${pn}`; // .padStart(3, '0')
};

/** Đọc sheet tra cứu "nhóm hàng" (cột A = Tên hàng, cột B = CODE) thành map
 *  tên hàng (đã chuẩn hoá) -> mã sản phẩm. Trả về map rỗng nếu không có sheet
 *  này trong file, để phần gọi không cần kiểm tra null riêng. */
const loadProductCodeLookup = (wb: XLSX.WorkBook): Record<string, string> => {
  const map: Record<string, string> = {};
  const sheetName = wb.SheetNames.find((n: string) => normalizeHeader(n) === PRODUCT_LOOKUP_SHEET_NAME);
  if (!sheetName) return map;

  const ws = wb.Sheets[sheetName];
  const matrix: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, blankrows: true });

  matrix.forEach((row: any[]) => {
    const name = normalizeProductName(row?.[0]);
    const code = cleanCode(row?.[1]).toUpperCase();
    if (name && code) map[name] = code;
  });

  return map;
};

/** Nút lọc danh sách lỗi ở panel bên phải */
const ErrorTab = ({
  active,
  count,
  label,
  tone,
  onClick
}: {
  active: boolean;
  count: number;
  label: string;
  tone: 'error' | 'warning';
  onClick: () => void;
}) => {
  const palette =
    tone === 'error'
      ? active
        ? 'bg-red-500 text-white border-red-500'
        : 'bg-white text-red-600 border-red-200 hover:bg-red-50'
      : active
      ? 'bg-amber-500 text-white border-amber-500'
      : 'bg-white text-amber-600 border-amber-200 hover:bg-amber-50';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0}
      className={`px-2 py-0.5 rounded text-[11px] font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${palette}`}
    >
      {label} ({count})
    </button>
  );
};

export default function ImportFileModal({ isOpen, year, month, callBackClose, callBackRefresh }: IProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const account = useSelector((state: RootState) => state.user.account);
  const branchs = useSelector((state: RootState) => state.select.branchSelectData);
  const branchName = (branchs || []).find((b: any) => b.id == account?.companyBranchId)?.name;
  const branchPrefix = getBranchPrefix(branchName);

  /* Wrapper Table không có prop height, chỉ có downHeight (đơn vị rem) và tự tính
     chiều cao grid = 100vh - downHeight. Nên ở đây đo vùng trống thật của modal
     rồi suy ngược ra downHeight, thay vì đoán một con số cứng.
     Nếu công thức trong wrapper khác, chỉ cần sửa lại đúng 1 dòng trong hàm update(). */
  const tableBoxRef = useRef<HTMLDivElement>(null);
  const [downHeight, setDownHeight] = useState<number>(20);

  const [period, setPeriod] = useState<moment.Moment>(() =>
    moment(`${year}-${String(month).padStart(2, '0')}`, 'YYYY-MM')
  );

  const selectedYear = period.year();
  const selectedMonth = period.month() + 1;

  const handlePeriodChange = (val: moment.Moment | null) => {
    if (!val) return;
    setPeriod(val);
    // đổi kỳ thì dữ liệu preview cũ không còn đúng nữa -> reset để tránh gửi nhầm tháng
    if (rows.length > 0) handleReset();
  };

  useLayoutEffect(() => {
    const el = tableBoxRef.current;
    if (!el) return undefined;

    const update = () => {
      const h = el.getBoundingClientRect().height;
      if (h <= 0) return;
      // chiều cao muốn có cho grid = h (đã trừ padding p-2) -> downHeight = (viewport - h) / 16
      setDownHeight(Math.max(0, (window.innerHeight - (h - 8)) / 16));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

  const [rows, setRows] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [sheetName, setSheetName] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');

  /* Lỗi do server trả về sau khi bấm Import */
  const [serverErrorCount, setServerErrorCount] = useState<number>(0);
  const [serverMessage, setServerMessage] = useState<string>('');
  const [onlyErrorRows, setOnlyErrorRows] = useState<boolean>(false);
  const [errorTab, setErrorTab] = useState<TErrorTab>('file');

  const clearServerErrors = () => {
    setServerErrorCount(0);
    setServerMessage('');
    setOnlyErrorRows(false);
  };

  const clearData = () => {
    setRows([]);
    setErrors([]);
    setWarnings([]);
    setSheetName('');
    setFileName('');
    setErrorTab('file');
    clearServerErrors();
  };

  /** Nút "Làm mới": xoá cả giá trị trong input file để chọn lại đúng file đó vẫn chạy */
  const handleReset = () => {
    if (inputRef.current) inputRef.current.value = '';
    clearData();
  };

  const readFile = (file: any) => {
    clearData();
    setFileName(file?.name || '');
    const reader = new FileReader();

    reader.onload = async (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });

        /* 1. Chọn sheet: ưu tiên sheet "thang <tháng đang chọn>", nếu không có lấy sheet đầu */
        const sheet =
          wb.SheetNames.find((n: string) => {
            const m = n.toLowerCase().match(/th[aá]ng?\s*(\d+)/);
            return m && Number(m[1]) === selectedMonth; // <-- đổi từ `month`
          }) ||
          wb.SheetNames.find((n: string) => /th[aá]ng?\s*\d+/i.test(n)) ||
          wb.SheetNames[0];

        setSheetName(sheet);
        const ws = wb.Sheets[sheet];

        /* 1.5. Bảng tra cứu Tên hàng -> Mã sản phẩm, dùng khi cột NHÓM HÀNG bị bỏ trống.
           Nếu file không có sheet "nhóm hàng" thì map rỗng, code phía dưới vẫn chạy
           bình thường (coi như không tra được, giữ nguyên warning như cũ). */
        const productCodeLookup = loadProductCodeLookup(wb);

        /* 2. Đọc dạng mảng để tự tìm dòng header (file mẫu header nằm ở dòng 2) */
        const matrix: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, blankrows: true });

        const headerIdx = matrix.findIndex((r: any[]) => (r || []).some((c: any) => normalizeHeader(c) === 'PN'));

        if (headerIdx < 0) {
          message.error('Không tìm thấy dòng tiêu đề (thiếu cột "PN") trong file');
          return;
        }

        const header = (matrix[headerIdx] || []).map(normalizeHeader);
        const fieldByCol: Record<number, string> = {};
        header.forEach((h: string, i: number) => {
          if (COLUMN_MAP[h]) fieldByCol[i] = COLUMN_MAP[h];
        });

        const errs: string[] = [];
        const warns: string[] = [];
        const parsed: any[] = [];

        for (let i = headerIdx + 1; i < matrix.length; i++) {
          const raw = matrix[i] || [];
          const excelRow = i + 1; // số dòng thực tế trong Excel

          const item: any = {};
          Object.keys(fieldByCol).forEach((k: any) => {
            item[fieldByCol[k]] = raw[k];
          });

          if (!toText(item.voucherNo) && !toText(item.productName)) continue;

          const returnDate = toDate(item.returnDate);
          parsed.push({
            excelRow,
            returnDate, // A
            voucherNo: TRA_HANG_IMPORT_SDT
              ? buildVoucherNo(branchPrefix, returnDate, cleanCode(item.voucherNo)) // B - STT phiếu -> SG2610102
              : cleanCode(item.voucherNo),
            productName: toText(item.productName), // C
            unit: toText(item.unit), // D
            rawQuantity: toNumber(item.quantity), // E
            unitPrice: toNumber(item.unitPrice), // F
            amount: toNumber(item.amount), // G
            saleName: toText(item.saleName), // H - tên hiển thị, chỉ để đối chiếu
            customerCode: cleanCode(item.customerCode), // I
            customerName: toText(item.customerName),
            customerPhoneNum: toPhone(item.customerPhoneNum),
            distributorName: toText(item.distributorName), // J
            orderCode: cleanCode(item.orderCode), // K
            note: toText(item.note), // L
            serial: toText(item.serial), // M
            carNo: toText(item.carNo), // N
            simSerial: toText(item.simSerial), // O
            simNo: toText(item.simNo), // P
            status: toText(item.status), // Q
            productCode: cleanCode(item.productCode).toUpperCase(), // R - gõ tay (có thể trống)
            productCodeGuessed: false, // đánh dấu nếu được tự tra thay vì gõ tay
            saleAccount: cleanCode(item.saleAccount) // S - username thật, gửi server
          });
        }

        /* 3. Tính SỐ LƯỢNG theo quy ước của file */
        const clusters: any[][] = [];
        parsed.forEach((it: any) => {
          if (it.rawQuantity !== null || clusters.length === 0) clusters.push([it]);
          else clusters[clusters.length - 1].push(it);
        });

        clusters.forEach((cluster: any[]) => {
          const head = cluster[0];
          if (!TRA_HANG_IMPORT_SDT && head.rawQuantity === null) {
            errs.push(`Dòng ${head.excelRow}: không xác định được số lượng (dòng đầu cụm bị trống SL NHẬP)`);
          }
          cluster.forEach((it: any) => {
            if (TRA_HANG_IMPORT_SDT) {
              // Mỗi dòng 1 sản phẩm, serial ghi chung 1 ô: có SL thì lấy SL, trống = 1.
              // Vd PN 104: bm 4g02 SL 3 -> 3, cam ahd 505 trống -> 1, cây cảm biến nhiệt trống -> 1
              it.quantity = it.rawQuantity !== null ? it.rawQuantity : 1;
            } else {
              it.quantity = cluster.length > 1 ? 1 : head.rawQuantity || 0;
            }

            /* Trong file, các dòng serial phía sau thường bỏ trống Mã KH / Mã đơn / Đại lý
               vì đã ghi ở dòng đầu cụm -> kéo xuống cho từng dòng, nếu không server sẽ
               báo thiếu thông tin ở mọi dòng chi tiết.
               Riêng Username KD (saleAccount) là trường TỰ NHẬP TAY và BẮT BUỘC trên
               từng dòng - không tự động kéo xuống từ dòng đầu cụm, để bắt lỗi đúng dòng
               nào thực sự bị bỏ trống thay vì "ăn theo" giá trị của dòng khác. */
            if (!it.customerCode) it.customerCode = head.customerCode;
            if (!it.orderCode) it.orderCode = head.orderCode;
            if (!it.distributorName) it.distributorName = head.distributorName;
            if (!it.customerName) it.customerName = head.customerName;
            if (!it.customerPhoneNum) it.customerPhoneNum = head.customerPhoneNum;

            /* NHÓM HÀNG: ưu tiên giá trị người dùng đã gõ tay trong cột này (cho phép
               override khi 1 tên hàng thực tế thuộc nhóm khác với bảng tra cứu chung).
               Chỉ khi ô này TRỐNG mới tự tra theo TÊN HÀNG ở sheet "nhóm hàng".
               Nếu tra không ra (tên hàng chưa có trong bảng / gõ sai chính tả) thì GIỮ
               NGUYÊN trống - không suy đoán bừa - để warning phía dưới vẫn bắt được và
               nhắc kế toán bổ sung, tránh việc "im lặng cho qua" dẫn tới sai nhóm hàng
               trong báo cáo. */
            if (!it.productCode) {
              const guess = productCodeLookup[normalizeProductName(it.productName)];
              if (guess) {
                it.productCode = guess;
                it.productCodeGuessed = true;
              }
            }
          });
          // if (cluster.length > 1 && head.rawQuantity && head.rawQuantity !== cluster.length) {
          //   warns.push(
          //     `Phiếu ${head.voucherNo} - dòng ${head.excelRow}: SL NHẬP (${head.rawQuantity}) khác số dòng chi tiết (${cluster.length})`
          //   );
          // }
        });

        /* 4. lineNo trong từng phiếu + rowNo (thứ tự gửi server) + validate */
        const counter: Record<string, number> = {};

        const items = parsed.map((it: any, idx: number) => {
          counter[it.voucherNo] = (counter[it.voucherNo] || 0) + 1;
          const lineNo = counter[it.voucherNo];

          if (!it.voucherNo) errs.push(`Dòng ${it.excelRow}: thiếu Số phiếu nhập (PN)`);
          else if (TRA_HANG_IMPORT_SDT && /^\d+$/.test(it.voucherNo) && !branchPrefix) {
            errs.push(
              `Dòng ${it.excelRow}: không xác định được ký hiệu chi nhánh (SG/HN/DN/CT) để ghép số phiếu - ${
                branchName
                  ? `chi nhánh "${branchName}" chưa có trong bảng ký hiệu`
                  : 'tài khoản chưa được gán chi nhánh'
              } `
            );
          }
          if (!it.returnDate) errs.push(`Dòng ${it.excelRow}: Ngày nhập không hợp lệ`);
          if (!it.productCode) {
            warns.push(
              `Dòng ${it.excelRow}: trống Nhóm hàng -> không có mã sản phẩm (không tra được "${it.productName}" trong sheet "nhóm hàng")`
            );
          }

          /* Bắt buộc có thông tin khách: Mã KH hoặc Mã đơn; bản mới thêm SĐT KH (server tự lấy mã KH từ SĐT,
             SĐT không có trên hệ thống thì server trả lỗi theo dòng). Kèm tên đại lý để kế toán dò ra khách. */
          const hasPhone = TRA_HANG_IMPORT_SDT && !!it.customerPhoneNum;
          const missingCustomer = !it.customerCode && !it.orderCode && !hasPhone;
          if (missingCustomer && REQUIRE_CUSTOMER_OR_ORDER) {
            errs.push(
              `Dòng ${it.excelRow}: thiếu cả ${TRA_HANG_IMPORT_SDT ? 'SĐT khách hàng' : 'Mã khách hàng'} và Mã đơn${
                it.distributorName ? ` - đại lý: ${it.distributorName}` : ''
              }`
            );
          }

          /* Bắt buộc có Username KD để server detach đúng tài khoản nhân viên kinh doanh.
             Cột "Kinh Doanh" (tên hiển thị) không dùng để suy ra được username. */
          const missingSaleAccount = !it.saleAccount;
          if (missingSaleAccount && REQUIRE_SALE_ACCOUNT) {
            errs.push(`Dòng ${it.excelRow}: thiếu Username kinh doanh${it.saleName ? ` - tên: ${it.saleName}` : ''}`);
          }

          return {
            ...it,
            lineNo,
            missingCustomer,
            missingSaleAccount,
            /* rowNo = vị trí trong mảng items gửi lên server (1-based) */
            rowNo: idx + 1,
            serverError: '',
            key: `${it.voucherNo}-${lineNo}-${it.excelRow}`,
            returnDateExport: it.returnDate ? moment(it.returnDate).format('DD/MM/YYYY') : ''
          };
        });

        /* 5. Tra Mã KH + Tên KH theo Mã đơn và SĐT để kế toán xem trước được khách trước khi import.
           Vd Mã đơn 261103007 / SĐT 0901234567 -> server trả { customerCode: 'KH001', customerName: 'Nguyễn Văn A' }
           -> điền vào dòng nếu ô đó đang trống (giá trị gõ tay trong file vẫn được giữ). Ưu tiên khách theo mã đơn. */
        // if (TRA_HANG_IMPORT_SDT) {
        //   const orderCodes: string[] = [];
        //   const phoneNums: string[] = [];
        //   items.forEach((it: any) => {
        //     if (it.orderCode && !orderCodes.includes(it.orderCode)) orderCodes.push(it.orderCode);
        //     if (it.customerPhoneNum && !phoneNums.includes(it.customerPhoneNum)) phoneNums.push(it.customerPhoneNum);
        //   });

        //   const [resOrder, resPhone]: any[] = await Promise.all([
        //     orderCodes.length > 0 ? DataManagementApi.GetCustomerInfoPerOrderCodes(orderCodes) : null,
        //     phoneNums.length > 0 ? DataManagementApi.GetCustomerInfoPerPhoneNum(phoneNums) : null
        //   ]);

        //   // Xét theo data thay vì cờ error: server có thể trả error = true kèm data (vd 1 vài mã không tìm thấy).
        //   // Không tra được thì vẫn cho import, server sẽ tự kiểm theo từng dòng.
        //   const infoByOrder = resOrder?.data?.data;
        //   const infoByPhone = resPhone?.data?.data;
        //   if (resOrder && !infoByOrder) {
        //     warns.push(`Không tra được khách theo Mã đơn: ${resOrder.data?.serverMessage || resOrder.data?.message}`);
        //   }
        //   if (resPhone && !infoByPhone) {
        //     warns.push(`Không tra được khách theo SĐT: ${resPhone.data?.serverMessage || resPhone.data?.message}`);
        //   }

        //   items.forEach((it: any) => {
        //     if (it.missingCustomer) return; // đã báo lỗi thiếu SĐT / mã đơn ở bước 4
        //     const khongThay: string[] = [];
        //     let traDu = true; // false = API tương ứng lỗi, chưa kết luận được

        //     // --- Theo Mã đơn ---
        //     if (it.orderCode) {
        //       const info = infoByOrder && infoByOrder[it.orderCode];
        //       if (info) {
        //         if (!it.customerCode) it.customerCode = info.customerCode || '';
        //         if (!it.customerName) it.customerName = info.customerName || '';
        //       } else if (infoByOrder) {
        //         khongThay.push(`Mã đơn ${it.orderCode}`);
        //       } else {
        //         traDu = false;
        //       }
        //     }

        //     // --- Theo SĐT ---
        //     if (it.customerPhoneNum) {
        //       const info = infoByPhone && infoByPhone[it.customerPhoneNum];
        //       if (info) {
        //         if (!it.customerCode) it.customerCode = info.customerCode || '';
        //         if (!it.customerName) it.customerName = info.customerName || '';
        //       } else if (infoByPhone) {
        //         khongThay.push(`SĐT ${it.customerPhoneNum}`);
        //       } else {
        //         traDu = false;
        //       }
        //     }

        //     if (khongThay.length === 0) return;
        //     const msg = `Dòng ${it.excelRow}: ${khongThay.join(', ')} không có trên hệ thống`;
        //     // Không xác định được khách nào -> không import được; còn khách theo nguồn khác thì chỉ cảnh báo
        //     if (!it.customerCode && traDu) errs.push(msg);
        //     else warns.push(msg);
        //   });
        // }

        if (TRA_HANG_IMPORT_SDT) {
          const orderCodes = new Set<string>();
          const phoneNums = new Set<string>();

          // 1. Phân loại data: Có mã đơn thì gom vào orderCodes, KHÔNG có mã đơn mới gom SĐT
          items.forEach((it: any) => {
            if (it.orderCode) {
              orderCodes.add(it.orderCode);
            } else if (it.customerPhoneNum) {
              phoneNums.add(it.customerPhoneNum);
            }
          });

          // 2. Vì 2 danh sách độc lập, gọi Promise.all để chạy song song cho nhanh nhất
          const [resOrder, resPhone]: any[] = await Promise.all([
            orderCodes.size > 0 ? DataManagementApi.GetCustomerInfoPerOrderCodes([...orderCodes]) : null,
            phoneNums.size > 0 ? DataManagementApi.GetCustomerInfoPerPhoneNum([...phoneNums]) : null
          ]);

          const infoByOrder = resOrder?.data?.data;
          const infoByPhone = resPhone?.data?.data;

          if (resOrder && !infoByOrder) {
            warns.push(`Không tra được khách theo Mã đơn: ${resOrder.data?.serverMessage || resOrder.data?.message}`);
          }
          if (resPhone && !infoByPhone) {
            warns.push(`Không tra được khách theo SĐT: ${resPhone.data?.serverMessage || resPhone.data?.message}`);
          }

          // 3. Quét lại items để ráp data tương ứng
          items.forEach((it: any) => {
            if (it.missingCustomer) return;

            const khongThay: string[] = [];
            let traDu = true;

            // Xét đúng theo tiêu chí phân loại lúc đầu
            if (it.orderCode) {
              // Đã có mã đơn -> Chỉ lấy data từ kết quả tra mã đơn
              const info = infoByOrder && infoByOrder[it.orderCode];
              if (info) {
                if (!it.customerCode) it.customerCode = info.customerCode || '';
                if (!it.customerName) it.customerName = info.customerName || '';
              } else if (infoByOrder) {
                khongThay.push(`Mã đơn ${it.orderCode}`);
              } else {
                traDu = false;
              }
            } else if (it.customerPhoneNum) {
              // Không có mã đơn, có SĐT -> Chỉ lấy data từ kết quả tra SĐT
              const info = infoByPhone && infoByPhone[it.customerPhoneNum];
              if (info) {
                if (!it.customerCode) it.customerCode = info.customerCode || '';
                if (!it.customerName) it.customerName = info.customerName || '';
              } else if (infoByPhone) {
                khongThay.push(`SĐT ${it.customerPhoneNum}`);
              } else {
                traDu = false;
              }
            }

            if (khongThay.length === 0) return;

            const msg = `Dòng ${it.excelRow}: ${khongThay.join(', ')} không có trên hệ thống`;
            if (!it.customerCode && traDu) errs.push(msg);
            else warns.push(msg);
          });
        }

        setRows(items);
        setErrors(errs);
        setWarnings(warns);
        setErrorTab(errs.length ? 'file' : 'warn');
      } catch (err) {
        console.error('Lỗi đọc file:', err);
        message.error('Không đọc được file Excel, vui lòng kiểm tra lại định dạng');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    readFile(file);
  };

  /** Gắn message lỗi của server vào đúng dòng theo rowNo */
  const applyServerErrors = (serverErrors: IServerRowError[]) => {
    const errorByRowNo: Record<number, string> = {};
    serverErrors.forEach(e => {
      if (!e || e.rowNo === undefined || e.rowNo === null) return;
      errorByRowNo[e.rowNo] = errorByRowNo[e.rowNo] ? `${errorByRowNo[e.rowNo]}; ${e.message}` : e.message;
    });

    setRows((prev: any[]) => prev.map((r: any) => ({ ...r, serverError: errorByRowNo[r.rowNo] || '' })));
    setServerErrorCount(Object.keys(errorByRowNo).length);
    setErrorTab('server');
    setOnlyErrorRows(false);
  };

  const handleFormSubmit = async () => {
    if (rows.length === 0) {
      message.warning('Vui lòng chọn file Excel');
      return;
    }
    if (errors.length > 0) {
      message.error('File còn dòng lỗi, vui lòng kiểm tra lại');
      return;
    }

    clearServerErrors();

    const dataBody = {
      year: selectedYear, // <-- đổi từ `year`
      month: selectedMonth, // <-- đổi từ `month`
      items: rows.map((r: any) => {
        const payload: any = { ...r };
        PREVIEW_ONLY_FIELDS.forEach(f => delete payload[f]);
        return payload;
      })
    };

    try {
      setConfirmLoading(true);
      const res = await DataManagementApi.CreateReturnProducts(dataBody);
      const body = res?.data;

      if (!body?.error) {
        message.success('Import file trả hàng thành công');
        callBackClose();
        callBackRefresh();
        return;
      }

      const rowErrors: IServerRowError[] = body?.data?.errors || [];
      const msg = body?.data?.message || body?.message || 'Import file trả hàng thất bại';
      setServerMessage(msg);

      if (rowErrors.length > 0) {
        applyServerErrors(rowErrors);
        message.error(`${msg} Xem các dòng được tô đỏ trong bảng.`);
      } else {
        message.error(msg);
      }
    } catch (error) {
      console.log(error);
      message.error('Import file trả hàng thất bại');
    } finally {
      setConfirmLoading(false);
    }
  };

  /** Lỗi server kèm số dòng Excel để kế toán mở file sửa đúng chỗ */
  const serverErrorList = useMemo(
    () => rows.filter((r: any) => r.serverError).map((r: any) => `Dòng Excel ${r.excelRow}: ${r.serverError}`),
    [rows]
  );

  /** Dòng bị coi là lỗi: server từ chối HOẶC thiếu Mã KH / Mã đơn HOẶC thiếu Username KD */
  const isErrorRow = (r: any) => !!r.serverError || !!r.missingCustomer || !!r.missingSaleAccount;

  const errorRowCount = useMemo(() => rows.filter(isErrorRow).length, [rows]);

  const dataTable = useMemo(() => (onlyErrorRows ? rows.filter(isErrorRow) : rows), [rows, onlyErrorRows]);

  const activeList = useMemo(() => {
    if (errorTab === 'server') return serverErrorList;
    if (errorTab === 'warn') return warnings;
    return errors;
  }, [errorTab, errors, warnings, serverErrorList]);

  const hasAnyIssue = errors.length > 0 || warnings.length > 0 || serverErrorList.length > 0;

  /* ---------------------------------------------------------------- */
  /* Cột preview: giữ ĐÚNG thứ tự A -> R của file Excel, cộng thêm     */
  /* Username KD (S) hiển thị ngay cạnh Kinh Doanh để đối chiếu        */
  /* ---------------------------------------------------------------- */
  const columns: IColumnTable[] = useMemo(
    () => [
      {
        dataField: 'serverError',
        caption: 'Lỗi từ hệ thống',
        width: 240,
        fixed: true,
        visible: serverErrorCount > 0,
        cellRender: (props: any) =>
          props.data.serverError ? (
            <span className="text-red-600 font-semibold">
              <CloseCircleOutlined /> {props.data.serverError}
            </span>
          ) : (
            <span className="text-green-600">
              <CheckCircleOutlined /> Hợp lệ
            </span>
          )
      },
      {
        dataField: 'excelRow',
        caption: 'Dòng Excel',
        width: 85,
        alignment: 'center',
        fixed: true,
        allowFiltering: false,
        cssClass: 'text-gray-400'
      },
      {
        dataField: 'lineNo',
        caption: 'Dòng',
        width: 60,
        alignment: 'center',
        allowFiltering: false,
        cssClass: 'font-semibold text-green-600'
      },
      {
        dataField: 'returnDateExport',
        caption: 'Ngày',
        width: 105,
        alignment: 'center'
        // fixed: true
      },
      {
        dataField: 'voucherNo',
        caption: 'PN',
        width: 100,
        alignment: 'center',
        // fixed: true,
        lookupFilter: true,
        cssClass: 'font-semibold'
      },

      {
        caption: 'Hàng Hoá',
        childHeader: [
          { dataField: 'productName', caption: 'Tên Hàng', width: 160 },
          { dataField: 'unit', caption: 'ĐV Tính', width: 80, alignment: 'center' },
          {
            dataField: 'rawQuantity',
            caption: 'SL Nhập',
            width: 85,
            alignment: 'right',
            format: 'fixedPoint',
            precision: 0,
            allowFiltering: false
          },
          {
            dataField: 'quantity',
            caption: 'SL Tính',
            width: 75,
            alignment: 'right',
            format: 'fixedPoint',
            precision: 0,
            allowFiltering: false,
            sumCol: 'quantity',
            isSumFooter: true,
            cssClass: 'font-semibold text-blue-600'
          },
          // File mới không còn Đơn giá / Thành tiền
          {
            dataField: 'unitPrice',
            caption: 'Đơn Giá',
            width: 110,
            alignment: 'right',
            format: 'fixedPoint',
            precision: 0,
            visible: !TRA_HANG_IMPORT_SDT
          },
          {
            dataField: 'amount',
            caption: 'Thành Tiền',
            width: 130,
            alignment: 'right',
            format: 'fixedPoint',
            precision: 0,
            sumCol: 'amount',
            isSumFooter: true,
            cssClass: 'font-semibold text-red-500',
            visible: !TRA_HANG_IMPORT_SDT
          }
        ]
      },
      {
        // caption: 'Nhân Viên KD',
        // childHeader: [
        // { dataField: 'saleName', caption: 'Tên KD', width: 100, lookupFilter: true },
        // {
        dataField: 'saleAccount',
        caption: 'Nhân Viên KD',
        width: 130,
        cellRender: (props: any) => {
          const { saleAccount, saleName } = props.data;
          if (saleAccount) return <span className="font-semibold">{saleAccount}</span>;
          return <span className="text-red-600 font-semibold">Thiếu username{saleName ? ` (${saleName})` : ''}</span>;
        }
        //   }
        // ]
      },
      {
        dataField: 'customerCode',
        caption: 'Mã KH',
        width: 150,
        cellRender: (props: any) => {
          const { customerCode, customerPhoneNum, orderCode } = props.data;
          if (customerCode) return <span>{customerCode}</span>;
          // Mã KH do server lấy từ SĐT lúc import
          if (TRA_HANG_IMPORT_SDT && customerPhoneNum) return <span className="text-gray-400">Theo SĐT</span>;
          if (orderCode) return <span className="text-gray-400">Theo mã đơn</span>;
          return (
            <span className="text-red-600 font-semibold">
              Thiếu {TRA_HANG_IMPORT_SDT ? 'SĐT KH' : 'mã KH'} / mã đơn
            </span>
          );
        }
      },
      { dataField: 'customerName', caption: 'Tên KH', width: 150, visible: TRA_HANG_IMPORT_SDT },
      { dataField: 'customerPhoneNum', caption: 'SĐT KH', width: 110, visible: TRA_HANG_IMPORT_SDT },
      { dataField: 'distributorName', caption: 'Đại Lý', width: 150 },
      { dataField: 'orderCode', caption: 'Mã Đơn', width: 120 },
      { dataField: 'note', caption: 'Ghi Chú', width: 140 },
      {
        caption: 'Serial',
        childHeader: [
          { dataField: 'serial', caption: 'Serial Nhận', width: 130 },
          { dataField: 'carNo', caption: 'Số Xe', width: 100 },
          { dataField: 'simSerial', caption: 'Serial Sim', width: 130 },
          { dataField: 'simNo', caption: 'Số Sim', width: 110 }
        ]
      },
      // { dataField: 'status', caption: 'Tình Trạng', width: 110 },
      {
        dataField: 'productCode',
        caption: 'Nhóm hàng',
        width: 210,
        lookupFilter: true,
        cellRender: (props: any) => {
          const { productName, productCode, productCodeGuessed } = props.data;
          if (!productCode) return <span className="text-orange-500 font-semibold">Trống</span>;

          /* Hiển thị "tên hàng - mã sản phẩm" để kế toán đối chiếu ngay tại cột này,
             không cần nhìn chéo qua cột "Tên Hàng" phía trước. */
          const label = productName ? `${productName} - ${productCode}` : productCode;

          if (productCodeGuessed) {
            return (
              <Tooltip title="Tự động tra theo Tên hàng ở sheet 'nhóm hàng' (cột NHÓM HÀNG đang để trống). Kiểm tra lại nếu không đúng.">
                <span className="text-blue-600">
                  {label}{' '}
                  <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, lineHeight: '16px' }}>
                    tự động
                  </Tag>
                </span>
              </Tooltip>
            );
          }
          return <span>{label}</span>;
        }
      }
    ],
    [serverErrorCount]
  );

  /** Tô nền dòng bị server từ chối / thiếu Mã KH / thiếu Username KD */
  const onRowPrepared = (e: any) => {
    if (e.rowType !== 'data') return;
    if (e.data?.serverError || e.data?.missingCustomer || e.data?.missingSaleAccount) {
      e.rowElement.style.backgroundColor = '#fff1f0';
      e.rowElement.style.color = '#f5222d';
    }
  };
  /** Panel danh sách lỗi ở cột phải */
  const renderErrorExcel = useMemo(() => {
    if (!hasAnyIssue) {
      return (
        <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-100 rounded-lg">
          <p className="text-xs text-gray-400 italic m-0">
            {rows.length === 0 ? 'Chưa có dữ liệu để kiểm tra' : 'Chưa phát hiện lỗi định dạng'}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="flex-1 overflow-y-auto custom-scrollbar rounded-lg border border-red-100 bg-red-50/40">
          {activeList.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-[11px] text-gray-400 italic m-0">Không có mục nào</p>
            </div>
          ) : (
            <ul className="space-y-1.5 m-0 p-3 list-none">
              {activeList.slice(0, 200).map((err, idx) => (
                <li
                  key={idx}
                  className={`flex gap-2 text-[11.5px] leading-relaxed ${
                    errorTab === 'warn' ? 'text-amber-700' : 'text-red-600'
                  }`}
                >
                  <span
                    className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                      errorTab === 'warn' ? 'bg-amber-400' : 'bg-red-400'
                    }`}
                  />
                  <span className="font-medium">{err}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </>
    );
  }, [hasAnyIssue, rows.length, errorTab, errors, warnings, serverErrorList, serverMessage, activeList]);

  const renderTable = useMemo(() => {
    if (rows.length === 0) {
      return (
        <div className="h-full m-2 flex items-center justify-center border-2 border-dashed border-gray-100 rounded-lg">
          <p className="text-xs text-gray-400 italic m-0">Chưa có dữ liệu xem trước - hãy chọn file Excel</p>
        </div>
      );
    }
    return (
      <div className="h-full w-full overflow-hidden">
        <Table
          tableName="Xem trước file trả hàng"
          tableId="trahang-import-preview"
          keyExpr="key"
          columns={columns}
          data={dataTable}
          isStriped={true}
          wrapTextData={false}
          hiddenAddButton={true}
          isHiddenToolbar={true}
          showImportExcel={false}
          allowExcel={false}
          reload={false}
          noHiddenPaging={true}
          enableLoadingPanel={false}
          downHeight={downHeight}
          onRowPreparedStyle={onRowPrepared}
          //
          scrollingMode="virtual"
          isHiddenPaging={false} // paging vẫn nên "enabled" nội bộ để DevExtreme chia chunk dữ liệu ảo
          minPageSize={100} // đóng vai trò kích thước mỗi "trang ảo" load thêm khi cuộn
        />
      </div>
    );
  }, [columns, dataTable, rows.length, downHeight]);

  return (
    <ModalComponent
      title={`Import file trả hàng - Tháng ${String(selectedMonth).padStart(2, '0')}/${selectedYear}`}
      visible={isOpen}
      onCancel={callBackClose}
      onOk={handleFormSubmit}
      confirmLoading={confirmLoading}
      okText={serverErrorCount > 0 ? 'Import lại' : 'Import'}
      okButtonProps={{ type: 'primary', disabled: rows.length === 0 || errors.length > 0 }}
      cancelText="Đóng"
      maskClosable={false}
      modalSize="fullscreen"
      destroyOnClose
    >
      {/* Khung ngoài: KHÔNG cuộn dọc, chia 2 tầng cố định -> bảng không bị giật */}
      <div className="flex flex-col h-[calc(92vh-3.8rem)] min-h-[80vh] w-full overflow-hidden">
        {/* ===== TẦNG 1: chọn file (trái) + danh sách lỗi (phải) ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 bg-white border-b shadow-sm min-h-[140px]">
          <div className="flex flex-col justify-between space-y-1">
            <div>
              <div className="flex items-center justify-between">
                <h4 className="m-0 text-lg font-semibold text-gray-800">{'Danh sách sản phẩm trả hàng'}</h4>
                <DatePicker
                  picker="month"
                  value={period}
                  onChange={handlePeriodChange}
                  format="MM/YYYY"
                  allowClear={false}
                  size="small"
                  className="w-[120px]"
                  locale={viVN.DatePicker}
                />
                <a
                  href={SAMPLE_FILE_URL}
                  download
                  className="px-3 py-1 text-[11px] font-medium text-green-600 transition-colors flex items-center gap-1"
                >
                  <i className="ri-download-line"></i> {'Tải file mẫu'}
                </a>
                <div className="hidden md:block w-20"></div>
              </div>
              <p className="m-0 text-[11.5px] text-gray-500">{'Quản lý sản phẩm trả hàng'}</p>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex-1">
                <input
                  ref={inputRef}
                  type="file"
                  name="xlfile"
                  id="xlf-return-product"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  className="block w-full text-xs text-gray-500
              file:mr-3 file:py-1.5 file:px-3
              file:rounded file:border file:border-gray-200
              file:bg-white file:text-gray-700
              file:text-xs file:font-medium
              hover:file:bg-gray-50 cursor-pointer transition-all"
                />
              </div>
              <div onClick={handleReset} className="hover:text-red-600 hover:underline p-2 border-1 cursor-pointer">
                Làm mới
              </div>
            </div>
          </div>

          <div className="relative border-l pl-2 flex flex-col">
            {renderErrorExcel ? (
              <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar max-h-[125px] text-[11.5px] italic">
                {renderErrorExcel}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center border-2 border-dashed border-gray-100 rounded-lg">
                <p className="text-xs text-gray-400 italic">Chưa phát hiện lỗi định dạng</p>
              </div>
            )}
          </div>
        </div>

        {/* ===== TẦNG 2: bảng preview chiếm hết phần còn lại ===== */}

        <div>{renderTable}</div>
      </div>
    </ModalComponent>
  );
}
